import { type CalculateMonthlyPaymentProps } from '@/features/payment-estimator/types/paymentEstimator'

export type FinancingDetails = {
  /** Cost of borrowing in cents */
  costOfBorrowing: number
  /** Total obligation in cents */
  totalObligation: number
}

/**
 * Calculates the financing details (cost of borrowing and total obligation) based on
 * the price, down payment, trade-in value, annual interest rate, and loan term.
 *
 * cost_of_borrowing = (monthly payment * loan term) - (vehicle price - down payment - trade-in value)
 * total_obligation = vehicle price + cost_of_borrowing
 *
 * @returns The financing details in cents, or null if the interest rate is invalid.
 */
export const calculateFinancingDetails = ({
  annualInterestRate,
  downPayment,
  loanTerm,
  price,
  tradeIn,
}: Omit<CalculateMonthlyPaymentProps, 'locale'>): FinancingDetails | null => {
  if (annualInterestRate > 100 || annualInterestRate < 0) {
    return null
  }

  const remainingTotal = price - downPayment - tradeIn

  if (remainingTotal <= 0) {
    return { costOfBorrowing: 0, totalObligation: price }
  }

  let monthlyPayment: number

  if (annualInterestRate === 0) {
    monthlyPayment = Math.round(remainingTotal / loanTerm)
  } else {
    const monthlyInterestRate = annualInterestRate / 12 / 100

    monthlyPayment =
      remainingTotal *
      ((monthlyInterestRate * Math.pow(1 + monthlyInterestRate, loanTerm)) /
        (Math.pow(1 + monthlyInterestRate, loanTerm) - 1))

    if (monthlyPayment < 0) {
      monthlyPayment = 0
    }

    monthlyPayment = Math.round(monthlyPayment)
  }

  const costOfBorrowing = Math.max(0, monthlyPayment * loanTerm - remainingTotal)
  const totalObligation = price + costOfBorrowing

  return { costOfBorrowing, totalObligation }
}
